// Blueprint: LP Material Slider ("20+ Profiloberflächen")
// Vollflaechen-Bildbuehne mit Kategorie-Pills oben (Tab-Navigation mit
// gleitendem Indikator) und weisser Verarbeitungs-Karte unten in voller Breite.
// Jede CMS-Zeile = eine Verarbeitung (Kategorie + optionale Gruppe + Label +
// Swatch + Hintergrundbild). Die Pill-Navigation wird aus den Kategorien der
// Zeilen gebaut (Reihenfolge des ersten Auftretens) und lebt in einem eigenen
// inneren Scroller, damit die Edge-Fades nur den Inhalt ausblenden, nicht die
// Glas-Pille selbst. Innerhalb der Karte werden die Verarbeitungen nach ihrer
// Gruppe zu horizontal scrollbaren Clustern zusammengefasst. Aktive Elemente
// werden in beiden Scrollern automatisch in den sichtbaren Bereich geschoben
// (Tab-Wechsel: zentriert, Swatch-Klick: minimal nachgezogen). Die Auswahl je
// Kategorie bleibt beim Tab-Wechsel erhalten; Zeilen ohne Hintergrundbild
// behalten das zuletzt eingeblendete.

export function initLpMaterialSlider() {
    document.querySelectorAll('.js-lpMaterialSlider').forEach((root) => {
        const nav = root.querySelector('.js-lpMaterialSliderNav');
        const variantsWrap = root.querySelector('.js-lpMaterialSliderVariants');
        const variants = Array.from(root.querySelectorAll('.js-lpMaterialSliderVariant'));
        const bgs = Array.from(root.querySelectorAll('.js-lpMaterialSliderBg'));
        if (!nav || !variantsWrap || !variants.length) return;

        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        const bgByVariant = new Map();
        variants.forEach((btn, i) => bgByVariant.set(btn, bgs[i] || null));

        const categories = [];
        variants.forEach((btn) => {
            const cat = btn.dataset.category || '';
            if (!categories.includes(cat)) categories.push(cat);
        });

        // ── Verarbeitungen zu Gruppen-Clustern zusammenfassen ────────────────────
        const groups = new Map();
        variants.forEach((btn) => {
            const cat = btn.dataset.category || '';
            const grp = btn.dataset.group || '';
            const key = `${cat} ${grp}`;
            let entry = groups.get(key);
            if (!entry) {
                const wrap = document.createElement('div');
                wrap.className = 'lpMaterialSlider__group';
                const label = document.createElement('span');
                label.className = 'lpMaterialSlider__groupLabel';
                label.textContent = grp;
                const items = document.createElement('div');
                items.className = 'lpMaterialSlider__groupItems';
                wrap.append(label, items);
                variantsWrap.appendChild(wrap);
                entry = { category: cat, wrap, items };
                groups.set(key, entry);
            }
            entry.items.appendChild(btn);
        });

        // ── Pill-Navigation: innerer Scroller + gleitender Indikator ─────────────
        const navScroller = document.createElement('div');
        navScroller.className = 'lpMaterialSlider__navScroller';
        nav.appendChild(navScroller);

        const indicator = document.createElement('span');
        indicator.className = 'lpMaterialSlider__navIndicator';
        indicator.setAttribute('aria-hidden', 'true');
        navScroller.appendChild(indicator);

        const tabs = categories.map((cat) => {
            const tab = document.createElement('button');
            tab.type = 'button';
            tab.className = 'lpMaterialSlider__navBtn';
            tab.setAttribute('role', 'tab');
            tab.textContent = cat;
            navScroller.appendChild(tab);
            return tab;
        });

        const moveIndicator = (tab) => {
            indicator.style.left = `${tab.offsetLeft}px`;
            indicator.style.width = `${tab.offsetWidth}px`;
        };

        // Aktives Element in den sichtbaren Bereich des Scrollers schieben:
        // center = mittig (Tab-/Kategorie-Wechsel), sonst nur minimal nachziehen.
        const revealInScroller = (scroller, el, { center = false, smooth = true } = {}) => {
            const pad = 16;
            const sRect = scroller.getBoundingClientRect();
            const eRect = el.getBoundingClientRect();
            let target = null;
            if (center) {
                target = scroller.scrollLeft + (eRect.left - sRect.left) - (sRect.width - eRect.width) / 2;
            } else if (eRect.left < sRect.left + pad) {
                target = scroller.scrollLeft + (eRect.left - sRect.left) - pad;
            } else if (eRect.right > sRect.right - pad) {
                target = scroller.scrollLeft + (eRect.right - sRect.right) + pad;
            }
            if (target === null) return;
            const instant = reduceMotion.matches || document.visibilityState === 'hidden';
            scroller.scrollTo({
                left: Math.max(0, target),
                behavior: smooth && !instant ? 'smooth' : 'auto',
            });
        };

        const selectedByCategory = new Map();

        const setVariant = (btn) => {
            variants.forEach((v) => {
                v.classList.toggle('is-active', v === btn);
                v.setAttribute('aria-pressed', v === btn ? 'true' : 'false');
            });
            selectedByCategory.set(btn.dataset.category || '', btn);
            const bg = bgByVariant.get(btn);
            if (bg && bg.querySelector('picture, img')) {
                bgs.forEach((b) => b.classList.toggle('is-active', b === bg));
            }
        };

        const setCategory = (cat, smooth = true) => {
            tabs.forEach((tab, i) => {
                const active = categories[i] === cat;
                tab.classList.toggle('is-active', active);
                tab.setAttribute('aria-selected', active ? 'true' : 'false');
                if (active) {
                    moveIndicator(tab);
                    revealInScroller(navScroller, tab, { center: true, smooth });
                }
            });
            groups.forEach((entry) => {
                entry.wrap.hidden = entry.category !== cat;
            });
            const remembered = selectedByCategory.get(cat);
            const fallback = variants.find((btn) => (btn.dataset.category || '') === cat);
            const next = remembered || fallback;
            if (next) {
                setVariant(next);
                revealInScroller(variantsWrap, next, { center: true, smooth });
            }
        };

        tabs.forEach((tab, i) => {
            tab.addEventListener('click', () => setCategory(categories[i]));
        });
        variants.forEach((btn) => {
            btn.addEventListener('click', () => {
                setVariant(btn);
                revealInScroller(variantsWrap, btn);
            });
        });

        // Indikator + Sichtbarkeit nach Layoutaenderungen nachfuehren (Fonts, Resize)
        window.addEventListener('resize', () => {
            const active = tabs.find((tab) => tab.classList.contains('is-active'));
            if (active) {
                moveIndicator(active);
                revealInScroller(navScroller, active, { center: true, smooth: false });
            }
        });

        setCategory(categories[0], false);
    });
}
